import { t, UUID, DateTimeISO, Status } from '../common.ts';

export const NotifyChannel = t.Union([
  t.Literal('line'),
  t.Literal('email'),
  t.Literal('sms'),
  t.Literal('inapp'),
  t.Literal('popup'),
]);

const AnnouncementBaseShape = {
  title: t.String(),
  body: t.String(),
  audience: t.Union([t.Literal('all'), t.Literal('group'), t.Literal('member'), t.Literal('partner')]),
  pinned: t.Boolean(),
  publishFrom: DateTimeISO,
  publishTo: t.Union([DateTimeISO, t.Null()]),
  status: Status,
} as const;

export const AnnouncementView = t.Object(
  {
    id: UUID,
    ...AnnouncementBaseShape,
    targetNodeId: t.Union([t.String(), t.Null()]),
    targetChain: t.Union([t.String(), t.Null()]),
    createdBy: t.Union([t.String(), t.Null()]),
    createdAt: DateTimeISO,
  },
  { $id: 'AnnouncementView' },
);

export const CreateAnnouncementRequest = t.Object(
  {
    ...AnnouncementBaseShape,
    // Empty/omitted = platform-wide (root only). Otherwise the target org id;
    // notify resolves + authorizes it against the creator's subtree at write.
    targetNodeId: t.Optional(t.Union([t.String(), t.Null()])),
  },
  { $id: 'CreateAnnouncementRequest' },
);

/**
 * Auth state a creative is aimed at.
 *
 * This is the honest version of the question `audience` was being asked to
 * answer. `audience` mixes auth state with targeting and its 'group' value
 * reaches nobody, so new surfaces (banners) carry only this.
 */
export const DisplayContext = t.Union([
  t.Literal('before_login'),
  t.Literal('after_login'),
  t.Literal('both'),
]);

export const PopupView = t.Object(
  {
    id: UUID,
    name: t.String(),
    imageUrl: t.String(),
    linkUrl: t.Union([t.String(), t.Null()]),
    // Optional rather than required: `CreateBody` is `t.Omit(PopupView, ['id'])`,
    // so making these mandatory would reject every existing caller's POST.
    // Omitted falls to the column defaults — 'both' and no cap, i.e. exactly the
    // pre-#231 behaviour.
    displayContext: t.Optional(DisplayContext),
    /** Impressions per calendar day per device. Null/omitted = uncapped. */
    maxShowsPerDay: t.Optional(t.Union([t.Integer({ minimum: 1 }), t.Null()])),
    displayFrom: DateTimeISO,
    displayTo: DateTimeISO,
    priority: t.Integer(),
    status: Status,
  },
  { $id: 'PopupView' },
);

/**
 * The banner at the top of the player home page: an image plus optional
 * player-facing copy, whose length caps are the design's (#574).
 *
 * Deliberately a narrower PopupView: no `audience`, because `displayContext`
 * answers the only question that surface ever needed answered.
 */
export const BannerView = t.Object(
  {
    id: UUID,
    name: t.String(),
    imageUrl: t.String(),
    linkUrl: t.Union([t.String(), t.Null()]),
    title: t.Optional(t.Union([t.String({ maxLength: 10 }), t.Null()])),
    subtitle: t.Optional(t.Union([t.String({ maxLength: 25 }), t.Null()])),
    description: t.Optional(t.Union([t.String({ maxLength: 95 }), t.Null()])),
    displayContext: t.Optional(DisplayContext),
    displayFrom: DateTimeISO,
    displayTo: DateTimeISO,
    priority: t.Integer(),
    status: Status,
  },
  { $id: 'BannerView' },
);

export const NotificationTemplateView = t.Object(
  {
    id: UUID,
    code: t.String(),
    channel: NotifyChannel,
    subject: t.Union([t.String(), t.Null()]),
    body: t.String(),
    variables: t.Array(t.String()),
    status: Status,
  },
  { $id: 'NotificationTemplateView' },
);

export const NotificationLogView = t.Object(
  {
    id: UUID,
    channel: NotifyChannel,
    memberId: t.Union([UUID, t.Null()]),
    toAddress: t.String(),
    templateCode: t.Union([t.String(), t.Null()]),
    subject: t.Union([t.String(), t.Null()]),
    body: t.String(),
    status: t.Union([t.Literal('queued'), t.Literal('sent'), t.Literal('failed')]),
    errorMessage: t.Union([t.String(), t.Null()]),
    sentAt: t.Union([DateTimeISO, t.Null()]),
    createdAt: DateTimeISO,
  },
  { $id: 'NotificationLogView' },
);

export const SendBroadcastRequest = t.Object(
  {
    channel: NotifyChannel,
    templateCode: t.Optional(t.String()),
    subject: t.Optional(t.String()),
    body: t.String({ minLength: 1 }),
    audience: t.Union([t.Literal('all'), t.Literal('group'), t.Literal('member')]),
    // Bounded: this array becomes one queued notification row per entry, and it
    // was unbounded — a single request could enqueue an arbitrary number of
    // messages. 5,000 covers any real campaign; larger ones belong in a job.
    audienceIds: t.Array(UUID, { default: [], maxItems: 5000 }),
  },
  { $id: 'SendBroadcastRequest' },
);
